import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Trash2, Save, X, ArrowLeft, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["documenti", "applicazioni", "marketing", "compliance", "dati", "utility"];
const DIFFICULTIES = ["facile", "medio", "avanzato"];
const OUTPUT_TYPES = ["docx", "pdf", "xlsx", "html_app", "markdown"];
const FIELD_TYPES = ["text", "textarea", "number", "select", "checkbox", "file", "email"];
const PROVIDERS = [
  { value: "", label: "Default (anthropic / claude-sonnet-4-5)" },
  { value: "anthropic", label: "Anthropic Claude" },
  { value: "openai", label: "OpenAI GPT" },
  { value: "gemini", label: "Google Gemini" },
];

const EMPTY_RECIPE = {
  slug: "",
  name_it: "",
  description_it: "",
  category: "documenti",
  icon_name: "FileText",
  difficulty: "facile",
  is_published: true,
  input_schema: { fields: [] },
  agent_config: { system: "", output_type: "docx", llm_provider: "", llm_model: "" },
};

export default function RecipeEditor({ recipeId, onClose, onSaved }) {
  const [recipe, setRecipe] = useState(EMPTY_RECIPE);
  const [loading, setLoading] = useState(!!recipeId);
  const [saving, setSaving] = useState(false);
  const isNew = !recipeId;

  useEffect(() => {
    if (!recipeId) return;
    api.get("/admin/recipes").then((r) => {
      const r0 = (r.data.items || []).find((x) => x.id === recipeId);
      if (r0) {
        setRecipe({
          ...EMPTY_RECIPE,
          ...r0,
          agent_config: { ...EMPTY_RECIPE.agent_config, ...(r0.agent_config || {}) },
          input_schema: { fields: r0.input_schema?.fields || [] },
        });
      }
      setLoading(false);
    });
  }, [recipeId]);

  const updateField = (idx, patch) => {
    setRecipe((r) => {
      const fields = [...r.input_schema.fields];
      fields[idx] = { ...fields[idx], ...patch };
      return { ...r, input_schema: { fields } };
    });
  };
  const addField = () => {
    setRecipe((r) => ({
      ...r,
      input_schema: {
        fields: [...r.input_schema.fields, { name: `campo_${r.input_schema.fields.length + 1}`, label: "Nuovo campo", type: "text", required: false }],
      },
    }));
  };
  const removeField = (idx) => {
    setRecipe((r) => ({ ...r, input_schema: { fields: r.input_schema.fields.filter((_, i) => i !== idx) } }));
  };
  const moveField = (idx, dir) => {
    setRecipe((r) => {
      const fields = [...r.input_schema.fields];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= fields.length) return r;
      [fields[idx], fields[newIdx]] = [fields[newIdx], fields[idx]];
      return { ...r, input_schema: { fields } };
    });
  };

  const save = async () => {
    // basic validation
    if (!recipe.slug || !recipe.name_it || !recipe.description_it) {
      toast.error("Slug, nome e descrizione obbligatori");
      return;
    }
    if (!recipe.agent_config?.system) {
      toast.error("Il system prompt dell'agente è obbligatorio");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        slug: recipe.slug,
        name_it: recipe.name_it,
        description_it: recipe.description_it,
        category: recipe.category,
        icon_name: recipe.icon_name,
        difficulty: recipe.difficulty,
        input_schema: recipe.input_schema,
        agent_config: recipe.agent_config,
        is_published: recipe.is_published,
      };
      if (isNew) {
        await api.post("/admin/recipes", payload);
        toast.success("Ricetta creata");
      } else {
        await api.patch(`/admin/recipes/${recipeId}`, payload);
        toast.success("Ricetta aggiornata (nuova versione)");
      }
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="adr-skeleton h-96" />;

  return (
    <div className="fixed inset-0 z-40 bg-black/30 overflow-y-auto" onClick={onClose}>
      <div className="min-h-screen flex items-start justify-center py-8 px-4">
        <div className="bg-white rounded-xl w-full max-w-4xl shadow-xl" onClick={(e) => e.stopPropagation()}>
          <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between sticky top-0 bg-white rounded-t-xl z-10">
            <button onClick={onClose} className="flex items-center gap-1.5 text-sm text-[#555555] hover:text-[#0F3D6B]" data-testid="editor-close">
              <ArrowLeft className="w-4 h-4" />
              Annulla
            </button>
            <div className="serif text-lg font-semibold">
              {isNew ? "Nuova ricetta" : `Modifica · v${recipe.version || 1}`}
            </div>
            <button onClick={save} disabled={saving} className="adr-btn-primary text-sm" data-testid="editor-save">
              <Save className="w-4 h-4 inline mr-1.5" />
              {saving ? "Salvataggio..." : "Salva"}
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Metadata */}
            <section className="space-y-4">
              <h3 className="serif text-base font-semibold text-[#0F3D6B]">Metadati</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="adr-label">Slug (URL)</label>
                  <input className="adr-input" value={recipe.slug} onChange={(e) => setRecipe({ ...recipe, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} disabled={!isNew} data-testid="editor-slug" />
                </div>
                <div>
                  <label className="adr-label">Nome (italiano)</label>
                  <input className="adr-input" value={recipe.name_it} onChange={(e) => setRecipe({ ...recipe, name_it: e.target.value })} data-testid="editor-name" />
                </div>
                <div className="sm:col-span-2">
                  <label className="adr-label">Descrizione</label>
                  <textarea className="adr-input min-h-[60px]" value={recipe.description_it} onChange={(e) => setRecipe({ ...recipe, description_it: e.target.value })} data-testid="editor-desc" />
                </div>
                <div>
                  <label className="adr-label">Categoria</label>
                  <select className="adr-input" value={recipe.category} onChange={(e) => setRecipe({ ...recipe, category: e.target.value })} data-testid="editor-category">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="adr-label">Difficoltà</label>
                  <select className="adr-input" value={recipe.difficulty} onChange={(e) => setRecipe({ ...recipe, difficulty: e.target.value })}>
                    {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="adr-label">Icona (lucide-react)</label>
                  <input className="adr-input" value={recipe.icon_name} onChange={(e) => setRecipe({ ...recipe, icon_name: e.target.value })} placeholder="FileText, Briefcase, ..." />
                </div>
                <div className="flex items-center gap-3 pt-7">
                  <input type="checkbox" id="published" checked={recipe.is_published} onChange={(e) => setRecipe({ ...recipe, is_published: e.target.checked })} className="w-4 h-4" />
                  <label htmlFor="published" className="text-sm">Pubblicata (visibile agli utenti)</label>
                </div>
              </div>
            </section>

            {/* Input fields */}
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="serif text-base font-semibold text-[#0F3D6B]">Campi di input</h3>
                <button onClick={addField} className="adr-btn-outline text-xs" data-testid="add-field">
                  <Plus className="w-3.5 h-3.5 inline mr-1" />
                  Aggiungi campo
                </button>
              </div>
              {recipe.input_schema.fields.length === 0 ? (
                <p className="text-sm text-[#555555] italic">Nessun campo. Clicca "Aggiungi campo" per iniziare.</p>
              ) : (
                <div className="space-y-3">
                  {recipe.input_schema.fields.map((f, idx) => (
                    <div key={idx} className="border border-[#E2E8F0] rounded-lg p-3 bg-[#F2F5F8]/40" data-testid={`field-row-${idx}`}>
                      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
                        <div className="sm:col-span-3">
                          <label className="text-xs text-[#555555]">Name (chiave)</label>
                          <input className="adr-input text-sm" value={f.name} onChange={(e) => updateField(idx, { name: e.target.value.replace(/[^a-z0-9_]/gi, "_") })} />
                        </div>
                        <div className="sm:col-span-4">
                          <label className="text-xs text-[#555555]">Label (visibile)</label>
                          <input className="adr-input text-sm" value={f.label} onChange={(e) => updateField(idx, { label: e.target.value })} />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="text-xs text-[#555555]">Tipo</label>
                          <select className="adr-input text-sm" value={f.type} onChange={(e) => updateField(idx, { type: e.target.value })}>
                            {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                        <div className="sm:col-span-2 flex items-center gap-2 pb-1">
                          <input type="checkbox" id={`req-${idx}`} checked={!!f.required} onChange={(e) => updateField(idx, { required: e.target.checked })} className="w-4 h-4" />
                          <label htmlFor={`req-${idx}`} className="text-xs">Obbligatorio</label>
                        </div>
                        <div className="sm:col-span-1 flex justify-end gap-1">
                          <button onClick={() => moveField(idx, -1)} className="text-xs text-[#555555] hover:text-[#0F3D6B] px-1.5" disabled={idx === 0} title="Su">↑</button>
                          <button onClick={() => moveField(idx, 1)} className="text-xs text-[#555555] hover:text-[#0F3D6B] px-1.5" disabled={idx === recipe.input_schema.fields.length - 1} title="Giù">↓</button>
                          <button onClick={() => removeField(idx)} className="text-[#C8102E] hover:bg-[#C8102E]/10 p-1.5 rounded" title="Rimuovi" data-testid={`remove-field-${idx}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      {f.type === "select" && (
                        <div className="mt-2">
                          <label className="text-xs text-[#555555]">Opzioni (una per riga)</label>
                          <textarea className="adr-input text-sm min-h-[60px]" value={(f.options || []).join("\n")} onChange={(e) => updateField(idx, { options: e.target.value.split("\n").map(s => s.trim()).filter(Boolean) })} />
                        </div>
                      )}
                      {f.type === "file" && (
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-[#555555]">Estensioni ammesse (.pdf,.docx,...)</label>
                            <input className="adr-input text-sm" value={(f.accept?.types || []).join(",")} onChange={(e) => updateField(idx, { accept: { ...(f.accept || {}), types: e.target.value.split(",").map(s => s.trim()).filter(Boolean) } })} />
                          </div>
                          <div>
                            <label className="text-xs text-[#555555]">Max MB</label>
                            <input type="number" className="adr-input text-sm" value={f.accept?.max_mb || 25} onChange={(e) => updateField(idx, { accept: { ...(f.accept || {}), max_mb: Number(e.target.value) } })} />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Agent config */}
            <section className="space-y-4">
              <h3 className="serif text-base font-semibold text-[#0F3D6B]">Configurazione agente</h3>
              <div>
                <label className="adr-label">System prompt</label>
                <textarea
                  className="adr-input min-h-[180px] font-mono text-sm"
                  value={recipe.agent_config.system || ""}
                  onChange={(e) => setRecipe({ ...recipe, agent_config: { ...recipe.agent_config, system: e.target.value } })}
                  data-testid="editor-system-prompt"
                />
                <div className="text-xs text-[#555555] mt-1">
                  L'agente deve restituire JSON. Includi nello system prompt lo schema atteso (es: {`{"titolo":str,"contenuto_markdown":str}`} oppure {`{"titolo":str,"html":str}`} per html_app).
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="adr-label">Tipo di output</label>
                  <select className="adr-input" value={recipe.agent_config.output_type || "docx"} onChange={(e) => setRecipe({ ...recipe, agent_config: { ...recipe.agent_config, output_type: e.target.value } })}>
                    {OUTPUT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="adr-label">Provider LLM</label>
                  <select className="adr-input" value={recipe.agent_config.llm_provider || ""} onChange={(e) => setRecipe({ ...recipe, agent_config: { ...recipe.agent_config, llm_provider: e.target.value } })}>
                    {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="adr-label">Modello (override)</label>
                  <input className="adr-input" placeholder="es. gpt-5.2, gemini-2.5-pro" value={recipe.agent_config.llm_model || ""} onChange={(e) => setRecipe({ ...recipe, agent_config: { ...recipe.agent_config, llm_model: e.target.value } })} />
                </div>
              </div>
              <div className="p-3 rounded-md bg-amber-50 border border-amber-200 flex gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-900 leading-relaxed">
                  Ad ogni salvataggio la ricetta incrementa la versione. Le modifiche sono visibili immediatamente agli utenti se la ricetta è "Pubblicata".
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
